import React, { useState } from 'react';

import { useIsViewer } from '../auth/hooks/useIsViewer';

import GreenButton from '../../components/GreenButton';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import SnapshotImportConfirmationDialog from '../snapshotImport/SnapshotImportConfirmationDialog';

import AnalyzerSnapshotsSettings from '../snapshots/SnapshotsSettings';
import AnalyzerSnapshotsSelector from './AnalyzerSnapshotsSelector';
import SnapshotExportDialog from '../snapshotExport/SnapshotExportDialog';

import { useAppSelector } from '../../app/hooks';
import { CreateSnapshotDto, Snapshot } from 'types';
import { AnalyzerMode, SnapshotClone } from './types';
import { Button, Grid, Paper, Tab, Tabs, Typography } from '@mui/material';
import { TabPanel } from '../singleTraceView/SettingsTabPanel';
import SnapshotHistorySettingsPicker from '../snapshotHistorySettings/SnapshotHistorySettingsPicker';
import AdvancedSnapshotSettings from '../snapshots/AdvancedSnapshotSettings';
import { useIsAdmin } from '../auth/hooks/useIsAdmin';
import { useIsSnapshotLocked } from './hooks/useIsSnapshotLocked';
import { useParseQueryParams } from './hooks/useParseQueryParams';

import { getAnalyzerType } from 'utils/analyzerTypes';
import { useAnalyzers } from './api/getAnalyzers';
import { useAnalyzer } from './api/getAnalyzer';
import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useUpdateAnalyzer } from './api/updateAnalyzer';
import { useCreateSnapshot } from 'features/snapshots/api/createSnapshot';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { useDeleteSnapshot } from 'features/snapshots/api/deleteSnapshot';
import { useIsGeoEnabled } from 'features/analyzerModules/api/getAnalyzerModules';
import { useSnapshotHistorySettings } from 'features/snapshotHistorySettings/api/getSnapshotHistorySettings';
import { useCreateSnapshotHistorySettings } from 'features/snapshotHistorySettings/api/createSnapshotHistorySettings';
import { LockOpen, LockOutlined } from '@mui/icons-material';
import SnapshotDetailsPanel from 'features/snapshotDetails/SnapshotDetailsPanel';

interface Props {
  inDialog?: boolean;
}

const AnalyzerSnapshotsPage = ({ inDialog }: Props) => {
  const isViewer = useIsViewer();
  const isAdmin = useIsAdmin();

  const { data: analyzers, isLoading: analyzersAreLoading } = useAnalyzers({});
  const selectedAnalyzerId = useAppSelector(
    (state) => state.analyzers.selectedAnalyzerId
  );
  const { data: selectedAnalyzer } = useAnalyzer({
    analyzerId: selectedAnalyzerId,
  });
  const selectedSnapshotId = useAppSelector(
    (state) => state.snapshots.selectedSnapshotId
  );
  const { data: selectedSnapshot } = useSnapshot({
    snapshotId: selectedSnapshotId,
  });
  const { data: snapshotHistorySettings } = useSnapshotHistorySettings({
    snapshotId: selectedSnapshotId,
  });
  const createHistorySettingsMutation = useCreateSnapshotHistorySettings({});
  const { data: snapshots } = useSnapshots({ analyzerId: selectedAnalyzerId });
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const createSnapshotMutation = useCreateSnapshot({
    analyzerId: selectedAnalyzerId,
    config: {
      // Set onMutate to do nothing here to avoid app crashes when trying to work with
      //  nonexistent snapshot IDs elsewhere on the page
      onMutate: () => {},
    },
  });
  const updateSnapshotMutation = useUpdateSnapshot({});
  const deleteSnapshotMutation = useDeleteSnapshot({
    analyzerId: selectedAnalyzerId,
  });

  const analyzerPropertyConstants = getAnalyzerType(
    selectedAnalyzer?.type || 0
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [snapshotImportDialogOpen, setSnapshotImportDialogOpen] =
    useState(false);
  const [importedSnapshotArray, setImportedSnapshotArray] = useState<
    SnapshotClone[]
  >([]);
  const [importedSnapshotIndex, setImportedSnapshotIndex] = useState(-1);
  const [existingImportedSnapshot, setExistingImportedSnapshot] = useState<
    null | Snapshot | undefined
  >(null);
  const [activeTab, setActiveTab] = useState(0);
  const [validatedSnapshotProperties, setValidatedSnapshotProperties] =
    useState<any | null>(null);

  const isSnapshotLocked = useIsSnapshotLocked(selectedSnapshot);

  const isGeoEnabled = useIsGeoEnabled(selectedAnalyzerId);

  useParseQueryParams({ setActiveTab });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleClickedNewSnapshot = async () => {
    if (!analyzerPropertyConstants) return;
    const newSnapshot: CreateSnapshotDto = {
      analyzer_id: selectedAnalyzerId,
      name: 'Default',
      port: 1,
      switch_port: 1,
      center: '1030 MHz',
      reference: -30,
      span: '300 MHz',
      scale: 10,
      rbw: analyzerPropertyConstants.RBW_VALUES[0],
      vbw: analyzerPropertyConstants.VBW_VALUES[0],
      atten: 'AUTO',
      preamp: analyzerPropertyConstants.PREAMP_VALUES.length > 1 ? 1 : 0,
      detection_mode:
        analyzerPropertyConstants.DETECTION_MODE_VALUES.indexOf('Positive'),
      lo: '0 MHz',
      lo_mode: 0,
      enabled: !!isGeoEnabled,
    };

    await createSnapshotMutation.mutateAsync({
      data: newSnapshot,
    });
  };

  const handleClickedDeleteSnapshot = async () => {
    await deleteSnapshotMutation.mutateAsync(selectedSnapshotId);
    if (!isGeoEnabled && selectedSnapshot?.crosspol_snapshot_id) {
      await updateAnalyzerMutation.mutateAsync({
        data: {
          id: selectedAnalyzerId,
          mode: AnalyzerMode.LIVE,
        },
        analyzerId: selectedAnalyzerId,
      });
    }
    deleteLinkedTemporarySnapshot(selectedSnapshotId);
  };

  const deleteLinkedTemporarySnapshot = (snapshotId: number) => {
    const linkedTemporarySnapshot = snapshots?.find(
      (snapshot) => snapshot.temporary_snapshot_id === snapshotId
    );
    if (!linkedTemporarySnapshot) return;
    deleteSnapshotMutation.mutate(linkedTemporarySnapshot.id);
    deleteLinkedTemporarySnapshot(linkedTemporarySnapshot.id);
  };

  const handleCloneClicked = async () => {
    if (
      !selectedSnapshot ||
      !selectedSnapshot.uuid ||
      !selectedSnapshot.name ||
      selectedSnapshot.preamp === null ||
      selectedSnapshot.preamp === undefined ||
      selectedSnapshot.scale === null ||
      selectedSnapshot.scale === undefined
    )
      return;

    const clonedTypedSnapshot: SnapshotClone = { ...selectedSnapshot };

    clonedTypedSnapshot['name'] = `${clonedTypedSnapshot.name} (CLONED)`;

    const clonedSnapshotResult = await createSnapshotMutation.mutateAsync({
      data: { ...clonedTypedSnapshot, recording_locked: false },
    });
    if (snapshotHistorySettings) {
      createHistorySettingsMutation.mutate({
        snapshotId: clonedSnapshotResult.id,
        data: snapshotHistorySettings,
      });
      if (clonedTypedSnapshot.recording_locked) {
        updateSnapshotMutation.mutate({
          snapshotId: clonedSnapshotResult.id,
          data: { recording_locked: true },
        });
      }
    }
  };

  const handleExportClicked = () => {
    setExportDialogOpen(true);
  };

  const handleImportClicked = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || !snapshots) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (!text || typeof text !== 'string') return;
      const parsedSnapshots: SnapshotClone[] = JSON.parse(text);
      const currentSnapshotUuidList = snapshots.map(
        (snapshot) => snapshot.uuid
      );

      setImportedSnapshotIndex(0);
      let snapshotArray: SnapshotClone[] = [];
      for (const snapshot of parsedSnapshots) {
        const { uuid } = snapshot;

        if (currentSnapshotUuidList.includes(uuid)) {
          snapshotArray = [...snapshotArray, snapshot];
          continue;
        }

        snapshot['analyzer_id'] = selectedAnalyzerId;
        await createSnapshotMutation.mutateAsync({
          data: snapshot,
        });
      }
      setImportedSnapshotArray(snapshotArray);
      getNextImportedSnapshotToCheck(snapshotArray);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const replaceSnapshotFromImport = async () => {
    if (!snapshots) return;
    const importedSnapshot = importedSnapshotArray[importedSnapshotIndex];
    const snapshotToReplace = snapshots.find(
      (snapshot) => snapshot.uuid === importedSnapshot.uuid
    );
    if (!snapshotToReplace) return;

    const { analyzer_id, ...importedSnapshotCopy } = importedSnapshot;

    await updateSnapshotMutation.mutateAsync({
      data: {
        ...importedSnapshotCopy,
      },
      snapshotId: snapshotToReplace.id,
    });
    getNextImportedSnapshotToCheck(importedSnapshotArray);
  };

  const addNewSnapshotFromImport = async () => {
    const importedSnapshot = importedSnapshotArray[importedSnapshotIndex];

    const { uuid, ...importedSnapshotCopy } = importedSnapshot;
    importedSnapshotCopy['analyzer_id'] = selectedAnalyzerId;
    await createSnapshotMutation.mutateAsync({
      data: importedSnapshotCopy,
    });
    getNextImportedSnapshotToCheck(importedSnapshotArray);
  };

  const getNextImportedSnapshotToCheck = (snapshotArray: SnapshotClone[]) => {
    if (importedSnapshotIndex === snapshotArray.length - 1) {
      setImportedSnapshotArray([]);
      setSnapshotImportDialogOpen(false);
      setImportedSnapshotIndex(-1);
      return;
    }
    let snapshotIndex = importedSnapshotIndex;
    snapshotIndex++;
    setImportedSnapshotIndex(snapshotIndex);
    const importedSnapshot = snapshotArray[snapshotIndex];
    const snapshotToReplace = snapshots?.find(
      (snapshot) => snapshot.uuid === importedSnapshot.uuid
    );
    setExistingImportedSnapshot(snapshotToReplace);
    setSnapshotImportDialogOpen(true);
  };

  if (analyzersAreLoading) return <></>;

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={`Snapshot ${selectedSnapshot?.name}`}
        onSubmit={handleClickedDeleteSnapshot}
        onClose={() => setDeleteDialogOpen(false)}
      />
      <SnapshotExportDialog
        selectedAnalyzerId={selectedAnalyzerId}
        open={exportDialogOpen}
        handleClose={() => setExportDialogOpen(false)}
      />
      <SnapshotImportConfirmationDialog
        open={snapshotImportDialogOpen}
        existingImportedSnapshot={existingImportedSnapshot}
        onReplace={replaceSnapshotFromImport}
        onNew={addNewSnapshotFromImport}
        handleClose={() => setSnapshotImportDialogOpen(false)}
      />
      <Grid container spacing={2} style={{ paddingBottom: '1.5rem' }}>
        {isAdmin && (
          <Grid container item spacing={1} style={{ margin: 0 }}>
            <Grid item style={{ paddingLeft: 0, paddingTop: 0 }}>
              <GreenButton
                name='New'
                onClick={handleClickedNewSnapshot}
                disabled={selectedAnalyzerId === -1 || isViewer}
              />
            </Grid>
            <Grid item style={{ paddingTop: 0 }}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                onClick={handleCloneClicked}
                disabled={selectedSnapshotId === -1 || isViewer}
              >
                Clone
              </Button>
            </Grid>
            <Grid item style={{ paddingTop: 0 }}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                onClick={handleExportClicked}
                disabled={selectedAnalyzerId === -1 || isViewer}
              >
                Export
              </Button>
            </Grid>
            <Grid item style={{ paddingTop: 0 }}>
              <input
                style={{ display: 'none' }}
                id='import-license-button'
                type='file'
                accept='.txt'
                onChange={handleImportClicked}
                disabled={selectedAnalyzerId === -1 || isViewer}
              />
              <label htmlFor='import-license-button'>
                <Button
                  color='primary'
                  variant='contained'
                  component='span'
                  disabled={selectedAnalyzerId === -1 || isViewer}
                >
                  Import
                </Button>
              </label>
            </Grid>
            <Grid item style={{ paddingTop: 0 }}>
              <Button
                type='submit'
                variant='contained'
                color='error'
                onClick={() => setDeleteDialogOpen(true)}
                disabled={selectedSnapshotId === -1 || isViewer}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        )}
        <Grid container item spacing={1} style={{ margin: 0 }}>
          <Grid
            item
            xs
            component={Paper}
            style={{
              boxShadow: inDialog ? '0px 0px 0px 0px #000' : undefined,
              minHeight: inDialog ? 'calc(80vh - 120px)' : 'calc(93vh - 120px)',
              maxHeight: 'calc(93vh - 120px)',
            }}
          >
            <AnalyzerSnapshotsSelector
              analyzers={analyzers}
              selectedAnalyzerId={selectedAnalyzerId}
              selectedSnapshotId={selectedSnapshotId}
              useModuleSelectors
              disableAnalyzerSelect={inDialog}
              setValidatedSnapshotProperties={setValidatedSnapshotProperties}
            />
          </Grid>
          <Grid
            item
            xs={6}
            component={Paper}
            style={{
              paddingBottom: '.8rem',
              boxShadow: inDialog ? '0px 0px 0px 0px #000' : undefined,
            }}
          >
            {selectedSnapshot ? (
              <>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab value={0} label='Basic' />
                  <Tab value={1} label='Advanced' />
                  {!isViewer && (
                    <Tab
                      value={2}
                      label='Recording'
                      icon={
                        selectedSnapshot.recording_locked ? (
                          <LockOutlined fontSize='small' color='error' />
                        ) : (
                          <LockOpen fontSize='small' />
                        )
                      }
                      iconPosition='end'
                      sx={{ minHeight: 0 }}
                    />
                  )}
                  <Tab value={3} label='Details' />
                </Tabs>
                <TabPanel value={activeTab} index={0}>
                  <AnalyzerSnapshotsSettings
                    selectedAnalyzerId={selectedAnalyzerId}
                    selectedSnapshot={selectedSnapshot}
                    editDisabled={isViewer || isSnapshotLocked}
                    validatedSnapshotProperties={validatedSnapshotProperties}
                    setValidatedSnapshotProperties={
                      setValidatedSnapshotProperties
                    }
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <AdvancedSnapshotSettings
                    selectedAnalyzerId={selectedAnalyzerId}
                    selectedSnapshot={selectedSnapshot}
                    editDisabled={isViewer || isSnapshotLocked}
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  {snapshots && !isViewer && (
                    <SnapshotHistorySettingsPicker
                      snapshots={snapshots}
                      selectedSnapshotId={selectedSnapshotId}
                    />
                  )}
                </TabPanel>
                <TabPanel value={activeTab} index={3}>
                  <SnapshotDetailsPanel
                    selectedSnapshotId={selectedSnapshotId}
                  />
                </TabPanel>
              </>
            ) : (
              <Grid item style={{ padding: '2rem' }}>
                <Typography color='GrayText'>
                  Select a snapshot to edit settings.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AnalyzerSnapshotsPage;
